import {
  MAX_TRACKED_SKIPPED_QUERIES,
  WALK_BUDGET_WINDOW_MS,
} from "./constants";
import type {
  SkipReport,
  WalkBudget,
  WalkBudgetDecision,
  WalkCategory,
} from "./types";

/**
 * A fixed-window rate limiter for the result-graph walk.
 *
 * The walk is synchronous, so its cost is blocking CPU. Without a ceiling, the
 * amount of blocking work the instrument does would scale with how many
 * queries qualify — and the queries that qualify are most numerous exactly
 * when the pod is least able to afford them. An instrument that gets more
 * expensive the closer the process is to death can accelerate the crash it
 * exists to explain, and inflate the very durations it is measuring.
 *
 * A ceiling is cheap to reason about: at most `maxWalksPerSecond` walks per
 * window, each bounded by the node cap, so the instrument's worst-case CPU is
 * a constant that does not depend on load or pod health.
 *
 * Diagnostically this loses nothing, provided the losses are legible. Denied
 * row-set walks are counted *and named* (`model.operation`, never values), so
 * a query that is being rate-limited into silence still appears in the data
 * as a name and a count rather than as nothing at all.
 *
 * **This budget is also what makes it safe to walk single results.** There is
 * no row count to gate a `findUnique` on, so admitting them means admitting a
 * very high-frequency category. Since the budget already pins worst-case CPU,
 * admitting them changes only *which* queries get sampled, not how much work
 * is done. The one new risk is starvation — `findUnique`s crowding out the
 * `findMany`s — so single results may claim at most half the window
 * (`floor(max / 2)`), leaving the larger share reserved for row sets.
 */
export const createWalkBudget = (maxWalksPerSecond: number): WalkBudget => {
  const singleResultLimit = Math.floor(maxWalksPerSecond / 2);

  let windowStartedAt = 0;
  let walksInWindow = 0;
  let singleResultWalksInWindow = 0;
  let skippedRowSetWalks = 0;
  let skippedRowSetQueries = new Set<string>();

  return {
    tryConsume: (
      category: WalkCategory,
      model: string | undefined,
      operation: string,
    ): WalkBudgetDecision => {
      const denyRowSet = (): WalkBudgetDecision => {
        if (category === "row-set") {
          skippedRowSetWalks += 1;
          if (skippedRowSetQueries.size < MAX_TRACKED_SKIPPED_QUERIES) {
            // Built only on the denied path, so the granted path allocates
            // no string.
            skippedRowSetQueries.add(
              model === undefined ? operation : `${model}.${operation}`,
            );
          }
        }
        return "rate-limited";
      };

      if (maxWalksPerSecond <= 0) {
        return denyRowSet();
      }

      const now = Date.now();
      if (now - windowStartedAt >= WALK_BUDGET_WINDOW_MS) {
        windowStartedAt = now;
        walksInWindow = 0;
        singleResultWalksInWindow = 0;
      }

      if (walksInWindow >= maxWalksPerSecond) {
        return denyRowSet();
      }

      if (category === "single-result") {
        if (singleResultWalksInWindow >= singleResultLimit) {
          return "single-result-share-exhausted";
        }
        singleResultWalksInWindow += 1;
      }

      walksInWindow += 1;
      return "granted";
    },

    pendingSkippedCount: () => skippedRowSetWalks,

    drainSkipped: (): SkipReport => {
      const report = {
        count: skippedRowSetWalks,
        queries: [...skippedRowSetQueries],
      };
      skippedRowSetWalks = 0;
      skippedRowSetQueries = new Set<string>();

      return report;
    },
  };
};
